import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Sparkles,
  Microscope,
  Star,
  ArrowRight,
  Award,
  Users,
  Clock3,
  Camera,
  Shield,
  Heart,
  Stethoscope,
  CheckCircle,
  Play,
} from "lucide-react";
import { useState, useRef, useEffect } from "react";
import AnimatedSection from "@/components/common/AnimatedSection";
import TypewriterText from "@/components/common/TypewriterText";
import PageTransition from "@/components/layout/PageTransition";
import { clinicInfo, getWhatsappUrl } from "@/config/site";
import { staggerChild, staggerParent } from "@/lib/animations";
import first from "../assets/2.jpg";
import second from "../assets/3.jpg";
import third from "../assets/7.jpg";
import four from "../assets/6.jpg";

// ─── YOUTUBE VIDEO IDs ────────────────────────────────────────────────────────
const YT = {
  hero: "S7FTdBc1LEc",
  drVideo: "17ze7-Ad0A0",
  t1: "5wAKulzUfIc",
  t2: "LXS41-o-vyQ",
};

function ytEmbed(id, params = {}) {
  const base = `https://www.youtube.com/embed/${id}`;
  const defaults = { rel: 0, modestbranding: 1, playsinline: 1 };
  const merged = { ...defaults, ...params };
  const qs = Object.entries(merged)
    .map(([k, v]) => `${k}=${v}`)
    .join("&");
  return `${base}?${qs}`;
}

// ─── MOBILE HERO SLIDER ───────────────────────────────────────────────────────
const mobileHeroSlides = [first, second, third];

function MobileHeroSlider() {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % mobileHeroSlides.length);
    }, 3500);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
      {mobileHeroSlides.map((src, i) => (
        <img
          key={i}
          src={src}
          alt=""
          className="absolute inset-0 h-full w-full object-cover transition-opacity duration-1000"
          style={{ opacity: i === current ? 1 : 0 }}
        />
      ))}
      {/* Dot indicators */}
      <div className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 gap-2">
        {mobileHeroSlides.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            className="h-1.5 rounded-full transition-all duration-300"
            style={{
              width: i === current ? "24px" : "6px",
              background: i === current ? "#ffd700" : "rgba(255,255,255,0.5)",
            }}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

const serviceCards = [
  {
    title: "Dental Implants",
    text: "Single, multiple, and implant-supported overdenture solutions with precision planning.",
  },
  {
    title: "Prosthodontics",
    text: "Crowns, bridges, dentures, and full-mouth rehabilitation for long-term function.",
  },
  {
    title: "Cosmetic Dentistry",
    text: "Smile design and aesthetic restorations tailored to your facial harmony.",
  },
];

const reasons = [
  { icon: Star, title: "5.0 Patient Rating", text: "Trusted outcomes and warm care." },
  {
    icon: ShieldCheck,
    title: "High Sterilization Standards",
    text: "Strict hygiene protocols at every step.",
  },
  {
    icon: Microscope,
    title: "Advanced Technology",
    text: "Where responsible Dentistry meets advance technology",
  },
  {
    icon: Sparkles,
    title: "Comfort-First Treatment",
    text: "Gentle approach focused on confidence and comfort.",
  },
];

const testimonials = [
  {
    text: "I had a fantastic experience at Kavlekar Dental! The clinic was not only modern and clean, but it also had a very welcoming and hygienic environment. The staff were extremely professional and friendly, making sure I felt comfortable throughout my visit.",
    name: "Sunmeet",
    role: "Software Engineer",
  },
  {
    text: "My go to place for any dental treatment! I recently visited Dr. Kavelekar's clinic for my root canal treatment. It was performed under a rubber dam and with the help of a microscope, which showed the level of precision and advanced care they provide.",
    name: "Eshita Gupta",
    role: "Teacher",
  },
  {
    text: "The doctor is very kind and takes the time to explain the root cause of the issue and the treatment needed. During the procedure, she is gentle and checks in to ensure everything is fine. I had root canal treatment and filling, fully satisfied.",
    name: "Sunil Mudhol",
    role: "Business Owner",
  },
];

const videoTestimonials = [
  {
    name: "Rajesh Kumar",
    role: "Patient – Dental Implant",
    youtubeId: YT.t1,
    quote: "The implant procedure was painless and the results are incredible.",
  },
  {
    name: "Priya Desai",
    role: "Patient – Smile Makeover",
    youtubeId: YT.t2,
    quote: "I finally have the smile I always dreamed of. Thank you, Dr. Kavlekar!",
  },
  {
    name: "Anil Sharma",
    role: "Patient – Full Mouth Rehab",
    youtubeId: null,
    poster: third,
    quote: "World-class treatment with such personal care. Highly recommend.",
  },
];

const imageShowcase = [
  { title: "Interior Facade", image: first },
  { title: "Comfortable Patient Area", image: second },
  { title: "Advance Dental Setup", image: third },
  { title: "Advance Dental Setup", image: four },
];

const stats = [
  { icon: Award, label: "Google Rating", value: "5.0★" },
  { icon: Users, label: "Happy Patients", value: "10K+" },
  { icon: Clock3, label: "Years of Clinical Focus", value: "12+" },
  { icon: Camera, label: "Transformation Cases", value: "450+" },
];

const treatmentJourney = [
  {
    icon: Stethoscope,
    title: "Step 1 · Diagnosis",
    text: "Comprehensive clinical examination and digital records for accurate case understanding.",
  },
  {
    icon: Shield,
    title: "Step 2 · Safe Planning",
    text: "A sterile, protocol-driven plan that balances comfort, outcomes, and timeline transparency.",
  },
  {
    icon: Heart,
    title: "Step 3 · Precision Care",
    text: "Minimally stressful treatment powered by precision instruments and clear communication.",
  },
  {
    icon: CheckCircle,
    title: "Step 4 · Long-Term Support",
    text: "Post-treatment reviews and preventive guidance to maintain a healthy, confident smile.",
  },
];

const homeFaqs = [
  {
    question: "Do I need an appointment before visiting the clinic?",
    answer:
      "Yes, appointments are strongly recommended so we can reserve adequate consultation and treatment time for you.",
  },
  {
    question: "Are dental implant procedures painful?",
    answer:
      "With modern planning, anesthesia, and gentle protocol, most implant procedures are highly manageable and comfortable.",
  },
  {
    question: "How long does a smile makeover usually take?",
    answer:
      "Timelines vary by case, but many aesthetic plans complete in a few visits after detailed diagnosis and planning.",
  },
  {
    question: "Do you provide treatment plans for senior patients?",
    answer:
      "Yes, we provide geriatric-focused options considering overall health, bite function, and long-term maintenance comfort.",
  },
  {
    question: "Can I get full-mouth rehabilitation at Kavlekar Dental?",
    answer:
      "Absolutely. We offer prosthodontic and implant-supported full-mouth rehabilitation based on your oral condition.",
  },
  {
    question: "Do you use digital technology for diagnostics?",
    answer:
      "Yes, we use digital imaging and advanced tools to improve diagnostic clarity and treatment predictability.",
  },
  {
    question: "How do I start my treatment journey quickly?",
    answer:
      "You can message us directly on WhatsApp and our team will guide you through consultation, records, and next steps.",
  },
];

function HeroYouTubeBackground() {
  return (
    <div
      className="absolute inset-0 overflow-hidden"
      aria-hidden="true"
      style={{ zIndex: 0 }}
    >
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          width: "calc(100% + 120px)",
          height: "calc(100% + 120px)",
          minWidth: "177.78vh",
          minHeight: "56.25vw",
          transform: "translate(-50%, -50%)",
          pointerEvents: "none",
        }}
      >
        <iframe
          src={ytEmbed(YT.hero, {
            autoplay: 1,
            mute: 1,
            loop: 1,
            playlist: YT.hero,
            controls: 0,
            disablekb: 1,
            fs: 0,
            iv_load_policy: 3,
            cc_load_policy: 0,
          })}
          title="Hero background video"
          allow="autoplay; encrypted-media"
          frameBorder="0"
          style={{
            width: "100%",
            height: "100%",
            border: "none",
            pointerEvents: "none",
          }}
        />
      </div>
    </div>
  );
}

function VideoTestimonialCard({ item, index }) {
  const [playing, setPlaying] = useState(false);

  const embedSrc = item.youtubeId
    ? ytEmbed(item.youtubeId, {
        autoplay: 1,
        rel: 0,
        modestbranding: 1,
        playsinline: 1,
      })
    : null;

  useEffect(() => {
    if (window.instgrm) window.instgrm.Embeds.process();
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="group relative overflow-hidden rounded-2xl border border-[#f2e6c9] bg-white shadow-sm hover:shadow-lg transition-shadow duration-300"
    >
      <div className="relative aspect-[9/14] sm:aspect-[4/5] overflow-hidden bg-[#1a1a1a]">
        {playing && embedSrc ? (
          <iframe
            src={embedSrc}
            title={`Testimonial – ${item.name}`}
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            frameBorder="0"
            className="absolute inset-0 h-full w-full"
          />
        ) : (
          <>
            {item.youtubeId ? (
              <img
                src={`https://img.youtube.com/vi/${item.youtubeId}/maxresdefault.jpg`}
                alt={`Thumbnail – ${item.name}`}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                onError={(e) => {
                  e.currentTarget.src = `https://img.youtube.com/vi/${item.youtubeId}/hqdefault.jpg`;
                }}
              />
            ) : (
              <img
                src={item.poster}
                alt={item.name}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a]/85 via-[#1a1a1a]/10 to-transparent pointer-events-none" />
            <button
              onClick={() => item.youtubeId && setPlaying(true)}
              disabled={!item.youtubeId}
              className="absolute inset-0 flex items-center justify-center disabled:cursor-not-allowed"
              aria-label={`Play testimonial from ${item.name}`}
            >
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm border-2 border-white/50 transition-transform duration-300 hover:scale-110 hover:bg-white/30">
                <Play className="h-7 w-7 text-white fill-white ml-1" />
              </div>
            </button>
            <div className="absolute bottom-0 left-0 right-0 px-4 pb-4">
              <p className="font-[Manrope] text-xs text-white/90 italic leading-relaxed line-clamp-2 mb-2">
                "{item.quote}"
              </p>
              {!item.youtubeId && (
                <span className="font-[Manrope] text-[10px] uppercase tracking-widest text-white/50">
                  Video coming soon
                </span>
              )}
            </div>
          </>
        )}
      </div>
      <div className="flex items-center gap-3 px-4 py-3 border-t border-[#f2e6c9]">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#f9f4e8] text-[#c5a059] font-bold text-sm">
          {item.name.charAt(0)}
        </div>
        <div className="min-w-0">
          <p className="font-[Manrope] text-sm font-semibold text-[#1a1a1a] truncate">{item.name}</p>
          <p className="font-[Manrope] text-[11px] text-[#888] truncate">{item.role}</p>
        </div>
        <div className="ml-auto flex gap-0.5 text-[#c5a059] text-xs shrink-0">
          {Array.from({ length: 5 }).map((_, i) => (
            <span key={i}>★</span>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

export default function HomePage() {
  return (
    <PageTransition testId="home-page">

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section
        className="relative isolate overflow-hidden w-full h-[70vh] md:h-screen"
        data-testid="home-hero-section"
      >
        {/* Mobile: image slider — hidden on md+ */}
        <div className="md:hidden absolute inset-0" style={{ zIndex: 0 }}>
          <MobileHeroSlider />
        </div>

        {/* Desktop: YouTube background — hidden on mobile */}
        <div className="hidden md:block absolute inset-0" style={{ zIndex: 0 }}>
          <HeroYouTubeBackground />
        </div>

        {/* Dark gradient overlay */}
        <div
          className="absolute inset-0 bg-gradient-to-r from-[#1a1a1a]/92 via-[#1a1a1a]/72 to-[#1a1a1a]/46"
          style={{ zIndex: 1 }}
        />

        {/* Hero content slot */}
        <div className="relative z-[2] flex h-full items-center justify-start px-6 md:px-16" />
      </section>

      <div className="overflow-x-hidden">
        <div className="mx-auto max-w-7xl space-y-20 px-6 pt-16 md:px-10 md:pt-24">

          {/* ── INTRO ─────────────────────────────────────────────────── */}
          <AnimatedSection testId="home-intro-section" className="grid gap-8 md:grid-cols-2">
            <div>
              <h2
                className="font-[Playfair_Display] text-3xl text-[#1a1a1a] md:text-5xl"
                data-testid="home-intro-title"
              >
                Advanced Dentistry with Human Warmth
              </h2>
            </div>
            <div>
              <p
                className="font-[Manrope] text-sm leading-relaxed text-[#555555] md:text-base"
                data-testid="home-intro-description"
              >
                {clinicInfo.name} is known for advanced dental implants, dentures,
                and microscopic precision treatment. Every care plan is personalized
                with focus on function, aesthetics, and long-term oral health.
              </p>
            </div>
          </AnimatedSection>

          {/* ── FEATURED SERVICES ─────────────────────────────────────── */}
          <AnimatedSection testId="home-services-grid-section">
            <div className="flex items-end justify-between gap-4">
              <h3
                className="font-[Playfair_Display] text-3xl text-[#1a1a1a] md:text-4xl"
                data-testid="home-services-title"
              >
                Featured Services
              </h3>
              <Link
                to="/services"
                className="inline-flex items-center gap-1 font-[Manrope] text-sm text-[#b08d45]"
                data-testid="home-services-explore-link"
              >
                Explore all <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <motion.div
              variants={staggerParent}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="mt-8 grid gap-6 md:grid-cols-3"
              data-testid="home-service-cards-grid"
            >
              {serviceCards.map((service) => (
                <motion.article
                  key={service.title}
                  variants={staggerChild}
                  whileHover={{ y: -5, scale: 1.01 }}
                  transition={{ duration: 0.25 }}
                  className="rounded-2xl border border-[#f2e6c9] bg-white p-7 shadow-[0_10px_30px_rgba(0,0,0,0.05)]"
                  data-testid={`home-service-card-${service.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <h4 className="font-[Playfair_Display] text-2xl text-[#1a1a1a]">
                    {service.title}
                  </h4>
                  <p className="mt-3 font-[Manrope] text-sm text-[#5b5b5b]">
                    {service.text}
                  </p>
                </motion.article>
              ))}
            </motion.div>
          </AnimatedSection>

          {/* ── WHY CHOOSE ────────────────────────────────────────────── */}
          <AnimatedSection testId="home-why-choose-section">
            <h3
              className="font-[Playfair_Display] text-3xl text-[#1a1a1a] md:text-4xl"
              data-testid="home-why-choose-title"
            >
              Why Choose Kavlekar Dental
            </h3>
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {reasons.map((reason) => {
                const Icon = reason.icon;
                const slug = reason.title.toLowerCase().replace(/\s+/g, "-");
                return (
                  <motion.div
                    key={reason.title}
                    whileHover={{ y: -6, scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                    className="flex flex-col items-center justify-start rounded-2xl bg-[#c5a059] p-6 text-center shadow-md"
                    data-testid={`home-reason-card-${slug}`}
                  >
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white shadow-md">
                      <Icon className="h-8 w-8 text-[#c5a059]" />
                    </div>
                    <h4
                      className="mt-4 font-[Playfair_Display] text-lg text-white"
                      data-testid={`home-reason-title-${slug}`}
                    >
                      {reason.title}
                    </h4>
                    <p
                      className="mt-2 font-[Manrope] text-sm text-white/85"
                      data-testid={`home-reason-text-${slug}`}
                    >
                      {reason.text}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </AnimatedSection>

          {/* ── WRITTEN TESTIMONIALS ──────────────────────────────────── */}
          <AnimatedSection testId="home-testimonials-section">
            <h3 className="font-[Playfair_Display] text-3xl text-center text-[#1a1a1a] md:text-4xl">
              Our Patients Say about our Services
            </h3>
            <div className="mt-12 max-w-6xl mx-auto grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {testimonials.map((item, index) => (
                <div
                  key={index}
                  className="bg-white border border-[#f2e6c9] rounded-2xl p-6 shadow-sm hover:shadow-md transition flex flex-col"
                >
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-[#1a1a1a] text-base">{item.name}</h4>
                    <div className="flex gap-1 text-[#c5a059] text-sm">
                      {Array.from({ length: 5 }).map((_, i) => <span key={i}>★</span>)}
                    </div>
                  </div>
                  <div className="my-3 h-[1px] bg-[#f2e6c9]" />
                  <p className="text-[#555] text-sm leading-relaxed line-clamp-6">{item.text}</p>
                  <a
                    href="https://www.google.com/search?q=Kavlekar+Dental+hublu#lrd=0x3bb8d737731fa873:0x10ace9e8a5f43141,1"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <span className="mt-3 text-xs text-[#c5a059] font-semibold cursor-pointer hover:underline">
                      Read full review →
                    </span>
                  </a>
                </div>
              ))}
            </div>
            <div className="mt-10 text-center">
              <a
                href="https://www.google.com/search?q=Kavlekar+Dental+hublu#lrd=0x3bb8d737731fa873:0x10ace9e8a5f43141,1"
                target="_blank"
                rel="noreferrer"
                className="inline-block rounded-xl bg-[#c5a059] px-8 py-3 text-white font-semibold hover:bg-[#b8944f] transition"
              >
                See All Testimonials
              </a>
            </div>
          </AnimatedSection>

          {/* ── VIDEO TESTIMONIALS ────────────────────────────────────── */}
          <AnimatedSection testId="home-video-testimonials-section">
            <div className="flex flex-col items-center text-center gap-3">
              <span className="inline-block rounded-full border border-[#e6c77a] bg-[#fdf8ee] px-4 py-1 font-[Manrope] text-[11px] uppercase tracking-[0.2em] text-[#b08d45] font-semibold">
                Video Testimonials
              </span>
              <h3 className="font-[Playfair_Display] text-3xl text-[#1a1a1a] md:text-4xl">
                Hear It Directly from Our Patients
              </h3>
              <p className="max-w-xl font-[Manrope] text-sm text-[#666] leading-relaxed">
                Real stories from real people — watch how Kavlekar Dental has transformed smiles and lives.
              </p>
            </div>
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {videoTestimonials.map((item, index) => (
                <VideoTestimonialCard key={index} item={item} index={index} />
              ))}
            </div>
            <div className="mt-10 text-center">
              <a
                href="https://www.google.com/search?q=Kavlekar+Dental+hublu#lrd=0x3bb8d737731fa873:0x10ace9e8a5f43141,1"
                target="_blank"
                rel="noreferrer"
                className="inline-block rounded-xl bg-[#c5a059] px-8 py-3 text-white font-semibold hover:bg-[#b8944f] transition"
              >
                See All Video Testimonials
              </a>
            </div>
          </AnimatedSection>

          {/* ── INSTAGRAM REELS ───────────────────────────────────────── */}
          <AnimatedSection testId="home-instagram-reels-section">
            <div className="flex flex-col items-center text-center gap-3">
              <span className="inline-block rounded-full border border-[#e6c77a] bg-[#fdf8ee] px-4 py-1 font-[Manrope] text-[11px] uppercase tracking-[0.2em] text-[#b08d45] font-semibold">
                Instagram Reels
              </span>
              <h3 className="font-[Playfair_Display] text-3xl text-[#1a1a1a] md:text-4xl">
                Our Latest from Instagram
              </h3>
              <p className="max-w-xl font-[Manrope] text-sm text-[#666] leading-relaxed">
                Watch transformations, tips, and behind-the-scenes moments straight from our clinic.
              </p>
            </div>
            <div className="mt-10 grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
              {[
                "https://www.instagram.com/reel/DUyCEb6k4Rc/",
                "https://www.instagram.com/reel/DUCFl05kmUc/",
                "https://www.instagram.com/reel/C74mJsFyv5B/",
                "https://www.instagram.com/reel/DGDJYdqSGXJ/",
              ].map((url, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="overflow-hidden rounded-2xl border border-[#f2e6c9] bg-white shadow-sm p-2"
                >
                  <blockquote
                    className="instagram-media"
                    data-instgrm-permalink={url}
                    data-instgrm-version="14"
                    style={{ width: "100%", minHeight: "400px" }}
                  />
                </motion.div>
              ))}
            </div>
            <div className="mt-10 text-center">
              <a
                href="https://www.instagram.com/drkavlekar/reels/"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#f09433] via-[#dc2743] to-[#bc1888] px-8 py-3 text-white font-semibold font-[Manrope] text-sm hover:opacity-90 transition"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                  <circle cx="12" cy="12" r="4" />
                  <circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" stroke="none" />
                </svg>
                Find More Reels on Instagram
              </a>
            </div>
          </AnimatedSection>

          {/* ── IMAGE SHOWCASE ────────────────────────────────────────── */}
          <AnimatedSection testId="home-image-showcase-section">
            <div className="flex items-end justify-between gap-4">
              <h3
                className="font-[Playfair_Display] text-3xl text-[#1a1a1a] md:text-4xl"
                data-testid="home-image-showcase-title"
              >
                Experience the Clinic Atmosphere
              </h3>
              <p
                className="max-w-sm text-right font-[Manrope] text-sm text-[#7a7a7a]"
                data-testid="home-image-showcase-subtitle"
              >
                Spacious interiors, clean systems, and a calm environment designed for patient comfort.
              </p>
            </div>
            <div className="mt-8 grid gap-5 md:grid-cols-2">
              {imageShowcase.map((item, index) => (
                <motion.article
                  key={item.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.55, delay: index * 0.06 }}
                  whileHover={{ y: -6, scale: 1.01, rotate: index % 2 === 0 ? -0.4 : 0.4 }}
                  className="group overflow-hidden rounded-2xl border border-[#f2e6c9] bg-white"
                  data-testid={`home-image-showcase-card-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                  <div className="border-t border-[#f2e6c9] px-5 py-3">
                    <p className="font-[Manrope] text-sm font-semibold text-[#3f3f3f]">{item.title}</p>
                  </div>
                </motion.article>
              ))}
            </div>
          </AnimatedSection>

          {/* ── STATS ─────────────────────────────────────────────────── */}
          <AnimatedSection testId="home-stats-section">
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {stats.map((item) => {
                const Icon = item.icon;
                const slug = item.label.toLowerCase().replace(/\s+/g, "-");
                return (
                  <motion.article
                    key={item.label}
                    whileHover={{ scale: 1.03, y: -4 }}
                    className="pulse-gold-border rounded-2xl border border-[#e8d6ad] bg-[#fffdf8] p-6 flex flex-col items-center text-center"
                    data-testid={`home-stat-card-${slug}`}
                  >
                    <Icon className="h-12 w-12 text-[#c5a059]" />
                    <p className="mt-3 font-[Playfair_Display] text-4xl text-[#1a1a1a]">{item.value}</p>
                    <p className="mt-2 font-[Manrope] text-sm text-[#666666]">{item.label}</p>
                  </motion.article>
                );
              })}
            </div>
          </AnimatedSection>

          <hr className="bg-[#e6c77a]" />

          {/* ── TREATMENT JOURNEY ─────────────────────────────────────── */}
          <AnimatedSection testId="home-treatment-journey-section">
            <h3
              className="font-[Playfair_Display] text-3xl text-[#1a1a1a] md:text-4xl text-center"
              data-testid="home-treatment-journey-title"
            >
              Your Care Journey at Kavlekar Dental
            </h3>
            <div className="mt-7 grid gap-5 md:grid-cols-2">
              {treatmentJourney.map((step, index) => {
                const Icon = step.icon;
                const slug = step.title.toLowerCase().replace(/\s+/g, "-");
                return (
                  <motion.article
                    key={step.title}
                    initial={{ opacity: 0, x: index % 2 === 0 ? -22 : 22 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.08 }}
                    whileHover={{ y: -4 }}
                    className="rounded-2xl border border-[#f2e6c9] bg-white p-6 flex flex-col items-center text-center"
                    data-testid={`home-treatment-step-card-${slug}`}
                  >
                    <div className="inline-flex rounded-full bg-[#f9f4e8] p-3">
                      <Icon className="h-5 w-5 text-[#b08d45]" />
                    </div>
                    <h4 className="mt-4 font-[Playfair_Display] text-2xl text-[#1a1a1a]">{step.title}</h4>
                    <p className="mt-3 font-[Manrope] text-sm text-[#555555]">{step.text}</p>
                  </motion.article>
                );
              })}
            </div>
          </AnimatedSection>

          {/* ── PREMIUM CTA BANNER ────────────────────────────────────── */}
          <AnimatedSection testId="home-premium-note-section">
            <motion.div
              whileHover={{ scale: 1.01 }}
              className="rounded-3xl border border-[#e6c77a] bg-gradient-to-r from-[#caa64b] via-[#d4af37] to-[#b8962e] px-5 py-8 md:px-10 md:py-10 text-center"
            >
              <div className="flex flex-col items-center gap-4 md:gap-6">
                <p className="font-[Manrope] font-bold text-[10px] md:text-xs uppercase tracking-[0.2em] text-white/80">
                  Premium Promise
                </p>
                <h3 className="font-[Playfair_Display] text-lg sm:text-xl md:text-3xl text-white leading-snug max-w-xl">
                  Advanced care | Thoughtful communication | Lasting confidence.
                </h3>
                <a
                  href={getWhatsappUrl()}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full sm:w-[80%] md:w-[300px] text-center rounded-full bg-white text-[#b8962e] px-6 py-3 md:py-4 font-[Manrope] text-xs md:text-sm font-semibold uppercase tracking-[0.17em] transition-all duration-300 hover:bg-[#f5f5f5]"
                >
                  Talk to our team
                </a>
              </div>
            </motion.div>
          </AnimatedSection>

          {/* ── FOUNDERS NOTE ─────────────────────────────────────────── */}
          <AnimatedSection testId="home-founders-note-section">
            <div className="grid gap-6 md:gap-8 rounded-3xl border border-[#e8d6ad] bg-white p-5 md:p-10 md:grid-cols-[1.2fr_1fr]">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55 }}
                data-testid="home-founders-note-content"
              >
                <p className="font-[Manrope] text-[10px] md:text-xs uppercase font-bold tracking-[0.2em] text-[#8b7a55]">
                  Founders' Note
                </p>
                <h3 className="mt-3 font-bold text-xl sm:text-2xl md:text-3xl text-[#1a1a1a] leading-snug">
                  "Our goal is simple: world-class precision with a deeply human treatment experience."
                </h3>
                <p className="mt-4 font-[Manrope] text-sm md:text-base italic leading-relaxed text-[#555555]">
                  Every patient who enters Kavlekar Dental deserves clarity, comfort,
                  and confidence. We built this clinic around advanced implant and
                  prosthodontic excellence, supported by strict sterilization and
                  transparent communication.
                </p>
                <p className="mt-3 font-[Manrope] text-sm md:text-base italic leading-relaxed text-[#555555]">
                  Whether it is preventive care, smile aesthetics, or complex
                  rehabilitation, our promise is to treat each case with precision,
                  empathy, and long-term commitment.
                </p>
                <p className="mt-5 font-[Manrope] text-xs md:text-sm font-bold text-[#1a1a1a]">
                  — Dr. Abhishek Kavlekar &amp; Dr. Namita Kavlekar
                </p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55 }}
                className="pulse-gold-border overflow-hidden rounded-2xl border border-[#f2e6c9] bg-[#fffdf8]"
              >
                <div className="aspect-video w-full">
                  <iframe
                    src={ytEmbed(YT.drVideo, { controls: 1, rel: 0, modestbranding: 1 })}
                    title="Founders' message"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    frameBorder="0"
                    className="h-full w-full"
                    loading="lazy"
                  />
                </div>
                <p className="border-t border-[#f2e6c9] px-4 py-3 font-[Manrope] text-[11px] md:text-xs text-[#666666] text-center md:text-left">
                  Founders speaking about treatment philosophy.
                </p>
              </motion.div>
            </div>
          </AnimatedSection>

          {/* ── FAQ ───────────────────────────────────────────────────── */}
          <AnimatedSection testId="home-faq-section">
            <h3
              className="font-[Playfair_Display] text-3xl text-[#1a1a1a] md:text-4xl text-center"
              data-testid="home-faq-title"
            >
              Frequently Asked Questions
            </h3>
            <p
              className="mt-2 max-w-3xl mx-auto text-center font-[Manrope] text-sm text-[#666666]"
              data-testid="home-faq-subtitle"
            >
              Quick answers to common patient questions about consultation, treatment comfort, and planning.
            </p>
            <div className="mt-10 max-w-4xl mx-auto divide-y divide-[#f2e6c9] bg-white rounded-2xl border border-[#f2e6c9] px-5 md:px-8">
              {homeFaqs.map((faq, index) => (
                <motion.div
                  key={faq.question}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.38, delay: index * 0.04 }}
                >
                  <details className="group py-5">
                    <summary className="flex items-center justify-between cursor-pointer list-none">
                      <span className="font-[Playfair_Display] text-lg md:text-xl text-[#1a1a1a]">
                        {faq.question}
                      </span>
                      <span className="text-2xl font-light text-[#c5a059] transition-transform duration-300 group-open:rotate-45">
                        +
                      </span>
                    </summary>
                    <p className="mt-3 font-[Manrope] text-sm md:text-base text-[#555555] leading-relaxed">
                      {faq.answer}
                    </p>
                  </details>
                </motion.div>
              ))}
            </div>
          </AnimatedSection>

        </div>

        {/* ── FINAL CTA ─────────────────────────────────────────────────── */}
        <AnimatedSection
          className="mx-auto mt-20 max-w-7xl px-6 pb-8 md:px-10"
          testId="home-final-cta-section"
        >
          <div className="rounded-3xl bg-gradient-to-r from-[#1a1a1a] via-[#2d2a24] to-[#1a1a1a] px-7 py-12 md:px-12 text-center">
            <h3
              className="text-3xl md:text-4xl text-white font-bold uppercase"
              data-testid="home-final-cta-title"
            >
              Ready to Transform Your Smile?
            </h3>
            <p
              className="mt-4 font-[Manrope] text-sm md:text-base text-[#f2e6c9] italic max-w-2xl mx-auto"
              data-testid="home-final-cta-description"
            >
              Connect with our team for personalized consultation and a treatment path designed for your comfort and confidence.
            </p>
            <div className="flex justify-center">
              <a
                href={getWhatsappUrl()}
                target="_blank"
                rel="noreferrer"
                className="mt-6 inline-flex justify-center w-full max-w-md rounded-full bg-[#c5a059] px-8 py-4 font-[Manrope] text-sm font-semibold uppercase tracking-[0.17em] text-white transition-colors hover:bg-[#b08d45]"
                data-testid="home-final-cta-button"
              >
                Start on WhatsApp
              </a>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </PageTransition>
  );
}